import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { GetExperiment } from '@carvana/core-experiment';
import { getUserState } from '@carvana/core-auth';
import { setPaintedDoorUserInfo, setSaleEffectiveDate } from '@store/sar-store';
import { getOVPHeaderData, getOvpMfeFeed, setOvpReferenceType } from '../../store/OvpPageStore';
import { getGarageData } from '../../store/GaragePageStore';
import { getMyCarvanaState } from '../../store/AppStore';
import { OvpSpinner } from '../_common/spinner/Spinner';
import BackLink from '../_common/back-link/BackLink';
import Error from '../error/Error';
import OwnedVehiclePageContent from './OwnedVehiclePageContent';
import { OVPMainWrapper, OVPContentWrapper, OVPErrorPageWrapper } from './OwnedVehiclePage.styles';
import { OvpFeedTypes, ReferenceTypes, AnalyticEvents, Experiments } from '../../constants';
import { Analytics } from '../../services/analytics-service';
import { isMyCarsReskinFixed } from '../../utils/myCarsReskinExperiment';

const getReferenceTypeParam = (queryString: string): string | null => {
  let found: string | null = null;
  new URLSearchParams(queryString || '').forEach((value: string, key: string) => {
    if (found === null && key.toLowerCase() === 'referencetype') {
      found = value;
    }
  });
  return found;
};

// CONTROL (inventory-ltw-my-cars-reskin !== control_fixed): original parsing.
const parseControlParams = (params: any, search: any) => {
  const [vehicleId, referenceQuery] = decodeURIComponent(params).split('?');
  let referenceType = new URLSearchParams(referenceQuery).get('referenceType');
  if (!referenceType) {
    referenceType = new URLSearchParams(search).get('referenceType');
  }
  return { vehicleId, referenceType };
};

// CONTROL_FIXED (inventory-ltw-my-cars-reskin === control_fixed): hardened parsing.
const parseFixedParams = (params: any, search: any) => {
  let decodedParams = params || '';
  try {
    decodedParams = decodeURIComponent(params);
  } catch {
    decodedParams = params || '';
  }
  const [vehicleId, referenceQuery] = decodedParams.split('?');

  const rawReferenceType = getReferenceTypeParam(referenceQuery) ?? getReferenceTypeParam(search);
  const referenceType = rawReferenceType ? rawReferenceType.trim().toLowerCase() : rawReferenceType;
  return { vehicleId, referenceType };
};

const OwnedVehiclePage = () => {
  const { params }: any = useParams();
  const { search }: any = useLocation();

  const { vehicleId, referenceType } = isMyCarsReskinFixed()
    ? parseFixedParams(params, search)
    : parseControlParams(params, search);

  // whether the URL carried a usable referenceType on entry - the population the fix targets
  const referenceTypePresent = !!referenceType;
  const ovpType = referenceType === ReferenceTypes.VEHICLE ? 'Purchased' : 'Appraised';
  const [showLink, setShowLink] = useState(false);
  const [ovpData, setOvpData] = useState<any>([]);
  const [ovpFeed, setOvpFeed] = useState<any>([]);
  const [isOVPDataLoading, setIsOVPDataLoading] = useState(true);
  const [apiResponseError, setApiResponseError] = useState(false);
  const [oneYearPurchase, setOneYearPurchase] = useState(false);

  const getOVPData = async () => {
    // TODO: Need a light weight endpoint to make this one car only status check
    const ovpHeaderResponse = await getOVPHeaderData(Number(vehicleId));
    const ovpMfeResponse = await getOvpMfeFeed(Number(vehicleId));
    const { showBacklink } = await getGarageData();
    const { isOneYearPurchase } = await getMyCarvanaState();

    if (ovpHeaderResponse.ovpDataError || ovpMfeResponse.ovpMfeDataError || ovpHeaderResponse.length === 0) {
      setApiResponseError(true);
      const ovpErrorProps = {
        page: 'ovp',
        facadeOwned: !!ovpHeaderResponse.ovpDataError,
        facadeFeed: !!ovpMfeResponse.ovpMfeDataError,
        referenceTypePresent,
      };
      Analytics.trackEvent(AnalyticEvents.ovpErrorState, ovpErrorProps);
      datadogRum.addAction(AnalyticEvents.ovpErrorState, ovpErrorProps);
      setIsOVPDataLoading(false);
      return;
    }

    setShowLink(showBacklink);
    setOneYearPurchase(isOneYearPurchase);
    setOvpData(ovpHeaderResponse);
    const responseData = ovpHeaderResponse?.[0] || {};
    setPaintedDoorUserInfo(
      responseData?.vehicle?.year,
      responseData?.vehicle?.make,
      responseData?.vehicle?.model,
      responseData?.title || ''
    );

    if (ovpMfeResponse.length > 0) {
      setOvpFeed(ovpMfeResponse);
    }

    const userId = getUserState()?.user?.user_id;
    const hasSevenDayGuarantee = ovpMfeResponse?.find((x) => x?.type === OvpFeedTypes.SEVEN_DAY_GUARANTEE);
    const isSevenDayGuaranteeExpired =
      hasSevenDayGuarantee?.content?.sevenDayGuarantee?.expired ||
      hasSevenDayGuarantee?.content?.sevenDayGuarantee?.expired === undefined;
    const skipBucketing = !userId || isSevenDayGuaranteeExpired;
    const assignedBucket = await GetExperiment({
      name: Experiments.SEVEN_DAY_MONEY_BACK_GUARANTEE,
      identifier: userId,
      skipBucketing,
    });
    const sarsContent = ovpMfeResponse?.find((x) => x?.type === OvpFeedTypes.SERVICE_AND_REPAIR);
    setSaleEffectiveDate(sarsContent?.content?.serviceAndRepair?.saleEffectiveDate);

    datadogRum.addAction(AnalyticEvents.sevenDayMoneyBackGuaranteeExperiment, {
      hasSevenDayGuarantee,
      isSevenDayGuaranteeExpired,
      assignedBucket,
      skipBucketing,
    });

    const viewedOVPProps = {
      vehicleId,
      ovpType,
      is_impersonating: getUserState()?.impersonation?.is_impersonating,
      referenceTypePresent,
    };
    Analytics.trackEvent(AnalyticEvents.viewedOwnedVehiclePage, viewedOVPProps);
    datadogRum.addAction(AnalyticEvents.viewedOwnedVehiclePage, viewedOVPProps);

    setIsOVPDataLoading(false);
  };

  useEffect(() => {
    setOvpReferenceType(referenceType);
    getOVPData();

    return () => {
      setOvpData([]);
      setOvpFeed([]);
    };
  }, []);

  const ErrorPane = () => {
    const backLinkText = 'Back to My Cars';

    return (
      <OVPErrorPageWrapper>
        <BackLink backLinkText={backLinkText} backLinkRoute="/my-carvana" />
        <Error screen="ovp" />
      </OVPErrorPageWrapper>
    );
  };

  const OvpPane = () => {
    const vehicleIsAppraised = ovpFeed.some(
      (item: any) => item.type === OvpFeedTypes.ACTIVE_TRADE || item.type === OvpFeedTypes.EXPIRED_TRADE
    );

    const backLinkText = 'Back to My Cars';

    return (
      <OVPMainWrapper>
        {/* Experiments */}

        {/* Backlink */}

        {showLink && <BackLink backLinkText={backLinkText} backLinkRoute="/my-carvana" />}

        {/* API Request Not Complete */}
        {isOVPDataLoading ? (
          <OvpSpinner />
        ) : (
          <>
            {ovpData.length > 0 && (
              <OVPContentWrapper>
                <OwnedVehiclePageContent
                  ovpData={ovpData[0]}
                  referenceType={referenceType}
                  ovpFeed={ovpFeed}
                  oneYearPurchase={oneYearPurchase}
                />
              </OVPContentWrapper>
            )}
          </>
        )}
      </OVPMainWrapper>
    );
  };

  return <>{apiResponseError ? <ErrorPane /> : <OvpPane />}</>;
};

export default OwnedVehiclePage;
